import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import { useQueryContext } from '@sierralabs/query-ui';
import { TaskStatusDot, TaskStatusLabel } from '@sierralabs/workflow-ui';
import { getQueryValue } from '../component/useQueryDisplayValue';
import { useSiteRouteComponents } from '../site-route/useSiteRouteComponents';
const StyledSubContent = styled.div `
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
  .view-details {
    display: flex;
    flex-direction: column;
    .button {
      margin-top: 8px;
    }
  }
`;
// override columns and include render functions
export function useSiteTraceBoardColumns(component, columns) {
    var _a;
    const { queries } = useQueryContext();
    const routes = useSiteRouteComponents();
    // use key to re-evaluate when columns are updated
    // since the columns array does not change reference
    const columnsKey = (((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || [])
        .map(column => {
        // trigger updates when these values change
        const fields = column.itemFields || {};
        const filter = column.filter || {};
        const nav = column.routeNavigation || {};
        const keys = [
            column.id,
            column.queryId,
            fields.status,
            fields.workflowId,
            filter.field,
            filter.routeId,
            filter.routeParam,
            nav.field,
            nav.routeId,
            nav.routeParam
        ];
        return keys.join(' ');
    })
        .join(',');
    const componentColumns = useMemo(() => {
        var _a;
        return Array.from(((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsKey]);
    const getColumnQuery = useCallback((columnId) => {
        const column = componentColumns.find(column => column.id === columnId);
        const queryId = column === null || column === void 0 ? void 0 : column.queryId;
        return queryId ? queries.find(query => query.id === queryId) : undefined;
    }, [componentColumns, queries]);
    const getTaskFields = useCallback((item) => {
        const query = getColumnQuery(item.columnId);
        const column = componentColumns.find(column => column.id === item.columnId);
        const fields = (column === null || column === void 0 ? void 0 : column.itemFields) || {};
        const status = query && fields.status
            ? getQueryValue(item.data, query, fields.status)
            : undefined;
        const workflowId = query && fields.workflowId
            ? getQueryValue(item.data, query, fields.workflowId)
            : undefined;
        return { status, workflowId };
    }, [componentColumns, getColumnQuery]);
    // title icon
    const renderTitleIcon = useCallback((renderOptions) => {
        const { item, isCollapsed } = renderOptions;
        const task = getTaskFields(item);
        return isCollapsed && task.status ? (React.createElement(TaskStatusDot, { task: getTaskFields(item) })) : null;
    }, [getTaskFields]);
    // title accessory
    const renderTitleAccessory = useCallback((renderOptions) => {
        const { item, isCollapsed } = renderOptions;
        const task = getTaskFields(item);
        return !isCollapsed && task.status ? (React.createElement(TaskStatusLabel, { size: 'small', task: getTaskFields(item) })) : null;
    }, [getTaskFields]);
    // sub content
    const getItemPath = useCallback((item) => {
        var _a;
        const query = getColumnQuery(item.columnId);
        const column = componentColumns.find(column => column.id === item.columnId);
        const nav = (column === null || column === void 0 ? void 0 : column.routeNavigation) || {};
        const route = nav.routeId
            ? routes.find(route => route.id === nav.routeId)
            : undefined;
        const routePath = ((_a = route === null || route === void 0 ? void 0 : route.props) === null || _a === void 0 ? void 0 : _a.path) || '';
        if (!item.data || !nav.field || !query || !routePath) {
            return;
        }
        const value = getQueryValue(item.data, query, nav.field);
        const path = routePath.replace(`:${nav.routeParam}`, value);
        return path;
    }, [componentColumns, routes, getColumnQuery]);
    const renderSubContent = useCallback((renderOptions) => {
        const { item, isCollapsed, isSelected } = renderOptions;
        const classes = ['view-details'];
        !isSelected && classes.push('hover-only');
        const path = getItemPath(item) || '';
        if (!item.data || isCollapsed || !path) {
            return null;
        }
        return (React.createElement(StyledSubContent, null,
            React.createElement("div", { className: classes.join(' ') },
                React.createElement(Button, { to: path, component: Link, color: 'primary', variant: 'outlined', className: 'button' }, "View Details"))));
    }, [getItemPath]);
    const updatedColumns = useMemo(() => {
        return columns.map(column => {
            column = Object.assign(Object.assign({}, column), { renderSubContent,
                renderTitleIcon,
                renderTitleAccessory });
            return column;
        });
    }, [columns, renderSubContent, renderTitleAccessory, renderTitleIcon]);
    return { columns: updatedColumns, componentColumns };
}
//# sourceMappingURL=useSiteTraceBoardColumns.js.map